import { useState, useEffect } from 'react'
import { ChevronDown, ChevronRight, Trash2, Settings, Power, Pause, ExternalLink, BarChart3, GitFork, Scissors, Code2, TrendingUp, Cloud, Database, AlertCircle, CheckCircle, Loader2, FileText, FolderOpen, Copy, Check, Clock, FileCode, Image, Folder, Download, ShieldCheck, Zap, XCircle, Wrench, BookOpen, RotateCcw, Tag } from 'lucide-react'
import { NODE_REGISTRY } from '../../data/nodeRegistry'
import { POC_LOCKED } from '../../constants/pocConfig'
import { getAPIBaseUrl } from '../../environment'
import { fetchGateStatus } from '../../services/gates/gateReportService'
import FileUploadDropzone from './FileUploadDropzone'
import './NodeConfigPanel.css'

// Node types that get the Target Profile selector
const TARGET_PROFILE_NODE_TYPES = new Set([
  'service-generation',
  'pipeline-service-gen',
  'pre-flight',
  'pipeline-pre-flight',
  'generate-java-package',
  'pipeline-java-package',
])

// POC: These nodes always use the DB default — dropdown is read-only
const POC_LOCKED_PROFILE_NODES = new Set([
  'pre-flight',
  'pipeline-pre-flight',
  'generate-java-package',
  'pipeline-java-package',
])

/**
 * Fetches and renders a Target Profile dropdown for nodes that support it.
 * Stores the selected profile ID in node config as `target_profile_id`.
 *
 * When `disabled=true` (POC mode for Pre-Flight and Java Package nodes):
 *   - Fetches the account default profile from /target-profiles/default
 *   - Shows it as a read-only display (no user interaction)
 *   - Reports the default profile ID via onChange so it is saved in node config
 */
function TargetProfileSelector({ accountId, value, onChange, onProfileSelected, disabled }) {
  const [profiles, setProfiles] = useState([])
  const [defaultProfile, setDefaultProfile] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  useEffect(() => {
    if (!accountId) return
    setLoading(true)
    setError(null)

    const token = localStorage.getItem('modernizeit_token')
    const headers = { 'Content-Type': 'application/json' }
    if (token) headers['Authorization'] = `Bearer ${token}`

    if (disabled) {
      // POC mode: fetch only the default profile
      fetch(
        `${getAPIBaseUrl()}/target-profiles/default?account_id=${encodeURIComponent(accountId)}`,
        { headers }
      )
        .then(r => {
          if (!r.ok) throw new Error(`${r.status}`)
          return r.json()
        })
        .then(data => {
          setDefaultProfile(data)
          setLoading(false)
          // Auto-report the default profile ID to parent so node config is set
          if (onChange && data?.profile_id) onChange(data.profile_id)
        })
        .catch(err => {
          console.error('[TargetProfileSelector] default fetch error:', err)
          setError(err.message)
          setLoading(false)
        })
    } else {
      fetch(
        `${getAPIBaseUrl()}/target-profiles?account_id=${encodeURIComponent(accountId)}`,
        { headers }
      )
        .then(r => {
          if (!r.ok) throw new Error(`${r.status}`)
          return r.json()
        })
        .then(data => {
          setProfiles(data)
          setLoading(false)
        })
        .catch(err => {
          console.error('[TargetProfileSelector] fetch error:', err)
          setError(err.message)
          setLoading(false)
        })
    }
  }, [accountId, disabled]) // eslint-disable-line react-hooks/exhaustive-deps

  // Repair stale saved values (only in editable mode)
  useEffect(() => {
    if (disabled) return
    if (!profiles.length || !value) return
    if (profiles.find(p => p.profile_id === value)) return
    const match = profiles.find(p =>
      p.name === value ||
      (p.target_language && `${p.name} - ${p.target_language}` === value) ||
      (p.target_language && `${p.name} · ${p.target_language}` === value)
    )
    if (match) {
      console.warn(`[TargetProfileSelector] Repairing stale target_profile_id: "${value}" → "${match.profile_id}" (${match.name})`)
      onChange(match.profile_id)
    } else {
      console.warn(`[TargetProfileSelector] Unrecognised target_profile_id "${value}" — clearing`)
      onChange(null)
    }
  }, [profiles, value]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleChange = (e) => {
    const profileId = e.target.value || null
    const profile = profileId ? profiles.find(p => p.profile_id === profileId) || null : null
    console.log(`[TargetProfileSelector] selected → profile_id: "${profileId}", name: "${profile?.name || 'none'}"`)
    onChange(profileId)
    if (onProfileSelected) onProfileSelected(profile)
  }

  // POC read-only mode
  if (disabled) {
    return (
      <div className="config-field">
        <label>Target Profile</label>
        {loading ? (
          <div className="config-readonly-value">Loading…</div>
        ) : error ? (
          <div className="config-readonly-value" style={{ color: 'var(--color-error, #ef4444)' }}>Error loading profile</div>
        ) : (
          <div className="config-readonly-value">
            {defaultProfile?.name || '—'}
            {defaultProfile?.target_language ? ` · ${defaultProfile.target_language}` : ''}
          </div>
        )}
        <div className="field-help">Using default target profile (POC).</div>
      </div>
    )
  }

  return (
    <div className="config-field">
      <label>Target Profile</label>
      {loading ? (
        <select disabled><option>Loading profiles…</option></select>
      ) : error ? (
        <select disabled><option>Error loading profiles ({error})</option></select>
      ) : (
        <select
          value={value || ''}
          onChange={handleChange}
        >
          <option value="">— No profile selected —</option>
          {profiles.map(p => (
            <option key={p.profile_id} value={p.profile_id}>
              {p.name}{p.is_default ? ' (default)' : ''}{p.target_language ? ` · ${p.target_language}` : ''}
            </option>
          ))}
        </select>
      )}
      {!loading && !error && !value && (
        <div className="field-help target-profile-notice">
          ⚠ A Target Profile is required to run this node.
        </div>
      )}
    </div>
  )
}

// Categories that have specialized views
// View names must match keys in DesignerView's PHASE_VIEWS
const VIEWABLE_CATEGORIES = {
  // Existing views (preserved)
  analyze: { label: 'Dependency Graph', icon: BarChart3, view: 'analyze' },
  map: { label: 'Call Graph', icon: GitFork, view: 'map' },
  decompose: { label: 'Cluster Analysis', icon: Scissors, view: 'decompose' },
  // New views for pipeline nodes
  discover: { label: 'ROI Dashboard', icon: TrendingUp, view: 'discovery' },
  data: { label: 'Data Lineage', icon: Database, view: 'data' },
}

// Node types that have specialized views (in addition to categories)
// View names must match keys in DesignerView's PHASE_VIEWS
const VIEWABLE_NODE_TYPES = {
  'pipeline-dependencies': { label: 'Dependency Graph', icon: GitFork, view: 'map' },
  'pipeline-monolith': { label: 'Cluster Analysis', icon: Scissors, view: 'decompose' },
  'pipeline-discovery': { label: 'ROI Dashboard', icon: TrendingUp, view: 'discovery' },
  'pipeline-architecture': { label: 'AWS Diagram', icon: Cloud, view: 'architect' },
  'pipeline-data': { label: 'Data Lineage', icon: Database, view: 'data' },
  'pipeline-code-analysis': { label: 'Analysis', icon: BarChart3, view: 'analyze' },
  'pipeline-ai-naming': { label: 'Naming Report', icon: Tag, view: 'ai-naming-report' },
  'code-refactor': { label: 'Report', icon: BarChart3, view: 'refactor-report' },
  'pipeline-refactor': { label: 'Report', icon: BarChart3, view: 'refactor-report' },
  'pipeline-java-validation': { label: 'Validation Report', icon: ShieldCheck, view: 'java-validation-report' },
  'pipeline-pre-flight': { label: 'Advanced Mode', icon: Zap, view: 'gate-report', gateType: 'compile' },
  'pre-flight':          { label: 'Advanced Mode', icon: Zap, view: 'gate-report', gateType: 'compile' },
}

// Node types that support "Download Project (POC)" — works even when job is FAILED
const POC_BUNDLE_NODE_TYPES = new Set([
  'pre-flight',
  'pipeline-pre-flight',
  'java-packaging',
  'generate-java-package',
  'pipeline-java-package',
])

// Node types that generate downloadable artifacts
const DOWNLOADABLE_NODE_TYPES = new Set([
  'code-analysis',
  'pipeline-code-analysis',
  'pipeline-ai-naming',
  'code-refactor',
  'pipeline-refactor',
  'test-smart',
  'java-validation',
  'pipeline-java-validation',
  'pre-flight',
  'pipeline-pre-flight',
  'java-packaging',
  'generate-java-package',
  'pipeline-java-package',
  'pipeline-smart-tests',
  'service-generation',
  'pipeline-service-gen',
  'dependency-mapper',
  'pipeline-dependencies',
])

// Categories that have code editor view
const CODE_EDITOR_CATEGORIES = ['analyze', 'generate', 'refactor', 'ai']

// Node types that have code editor view
const CODE_EDITOR_NODE_TYPES = ['pipeline-code-analysis', 'pipeline-refactor', 'pipeline-service-gen']

// ─────────────────────────────────────────────────────────────────────────────
// Gate node types — Structural Check and Compile Check
// These nodes get a special status panel with remediation actions.
// ─────────────────────────────────────────────────────────────────────────────
const GATE_NODE_TYPES = {
  'pipeline-java-validation': { label: 'Structural Check',      gateType: 'structural' },
  'java-validation':          { label: 'Structural Check',      gateType: 'structural' },
  'pipeline-pre-flight':      { label: 'Compile Check',         gateType: 'compile'    },
  'pre-flight':               { label: 'Compile Check',         gateType: 'compile'    },
  'java-packaging':           { label: 'Package Compile Check', gateType: 'package'    },
  'pipeline-java-package':    { label: 'Package Compile Check', gateType: 'package'    },
}

// ─────────────────────────────────────────────────────────────────────────────
// Re-run Node — maps UI node types to API node_id values + result key lookup
// ─────────────────────────────────────────────────────────────────────────────

/** UI nodeType → API node_id (executor type string sent to POST /rerun-node) */
const NODE_TYPE_TO_API_ID = {
  'pre-flight':               'pre_flight',
  'pipeline-pre-flight':      'pre_flight',
  'java-validation':          'java_validation',
  'pipeline-java-validation': 'java_validation',
  'java-packaging':           'java_packaging',
  'pipeline-java-package':    'java_packaging',
  'service-generation':       'service_generation',
  'pipeline-service-gen':     'service_generation',
  'code-analysis':            'code_analysis',
  'pipeline-code-analysis':   'code_analysis',
  'ai-naming':                'ai_naming',
  'code-refactor':            'code_refactor',
  'pipeline-refactor':        'code_refactor',
  'data-analysis':            'data_analysis',
  'dependency-mapper':        'dependency_mapper',
  'discovery':                'discovery_roi',
  'architecture':             'architecture',
  'monolith-identifier':      'monolith_identifier',
  'smart-tests':              'test_smart',
}

/** API node_id → executionResult.jobs key used by gate/report panels */
const API_ID_TO_RESULT_KEY = {
  pre_flight:          'pre_flight_job_id',
  java_validation:     'java_validation_job_id',
  java_packaging:      'java_packaging_job_id',
  service_generation:  'service_generation_job_id',
  code_analysis:       'codeanalysis_job_id',
  ai_naming:           'ai_naming_job_id',
  code_refactor:       'code_refactor_job_id',
  data_analysis:       'data_analysis_job_id',
  dependency_mapper:   'dependency_mapper_job_id',
  discovery_roi:       'discovery_roi_job_id',
  architecture:        'architecture_job_id',
  monolith_identifier: 'monolith_identifier_job_id',
  test_smart:          'smart_tests_job_id',
}

/**
 * GateStatusPanel — status badge + counts + action buttons for gate nodes.
 * Shown in the right panel when a Structural Check or Compile Check node is selected.
 *
 * Uses gateReportService to fetch real gate status from S3 artifacts.
 * Grep: MOCK_UI_ONLY
 */
function GateStatusPanel({ node, gateInfo, onOpenView, executing, paused, executionProgress }) {
  const [gateStatus, setGateStatus] = useState(null)
  const [loading, setLoading]       = useState(false)
  const [actionMsg, setActionMsg]   = useState(null)

  const nodeResult = executionProgress[node?.id]?.result || node?.data?.executionResult
  const hasRun     = Boolean(nodeResult)
  const jobId      = nodeResult?.jobs?.java_validation_job_id ||
                     nodeResult?.jobs?.pre_flight_job_id ||
                     'no-job'

  const apiBase = getAPIBaseUrl()

  // Load gate status whenever jobId / gateType changes
  useEffect(() => {
    if (!hasRun) return
    setLoading(true)
    fetchGateStatus(jobId, gateInfo.gateType, apiBase)
      .then(st => { setGateStatus(st); setLoading(false) })
      .catch(() => { setLoading(false) })
  }, [jobId, gateInfo.gateType, apiBase, hasRun])

  const showActionMsg = (msg) => {
    setActionMsg(msg)
    setTimeout(() => setActionMsg(null), 3000)
  }

  const counts   = gateStatus?.counts  || { blocking: 0, warnings: 0, autoFixable: 0, needsDecision: 0 }
  const status   = gateStatus?.status  || (hasRun ? 'PASSED' : null)
  const canOpen  = hasRun
  const isActive = executing && !paused

  const statusCls = status === 'PASSED' ? 'passed' : status === 'WARNINGS' ? 'warnings' : 'blocked'
  const StatusIcon = status === 'PASSED' ? CheckCircle : status === 'WARNINGS' ? AlertCircle : XCircle

  const GateIcon = gateInfo.gateType === 'compile' ? Zap : ShieldCheck

  return (
    <div className="gate-status-panel">
      {/* Gate label + status */}
      <div className="gate-panel-header">
        <GateIcon size={14} />
        <span className="gate-panel-label">{gateInfo.label}</span>
        {loading && <Loader2 size={12} className="spin gate-spin" />}
        {!loading && status && (
          <span className={`gate-panel-badge ${statusCls}`}>
            <StatusIcon size={11} />
            {status === 'PASSED' ? 'Passed' : status === 'WARNINGS' ? 'Warnings' : 'Blocked'}
          </span>
        )}
        {!loading && !status && (
          <span className="gate-panel-badge idle">Not run</span>
        )}
      </div>

      {/* Counts */}
      {gateStatus && (
        <div className="gate-panel-counts">
          <div className="gate-count-item blocking">
            <span className="gate-count-val">{counts.blocking}</span>
            <span className="gate-count-lbl">Blockers</span>
          </div>
          <div className="gate-count-item warnings">
            <span className="gate-count-val">{counts.warnings}</span>
            <span className="gate-count-lbl">Warnings</span>
          </div>
          <div className="gate-count-item fixable">
            <span className="gate-count-val">{counts.autoFixable}</span>
            <span className="gate-count-lbl">Auto-fixable</span>
          </div>
          <div className="gate-count-item decision">
            <span className="gate-count-val">{counts.needsDecision}</span>
            <span className="gate-count-lbl">Need decision</span>
          </div>
        </div>
      )}

      {/* Action buttons */}
      <div className="gate-panel-actions">
        <button
          className="gate-action-btn primary"
          disabled={isActive || !canOpen || !onOpenView}
          title={canOpen ? `Open ${gateInfo.label} Report` : 'Run node first'}
          onClick={() => onOpenView?.('gate-report', { ...node.data, _gateType: gateInfo.gateType })}
        >
          <BookOpen size={13} />
          Open Report
        </button>

        <button
          className="gate-action-btn"
          disabled={isActive || !canOpen || !onOpenView}
          title={canOpen ? 'Open Fix & Continue workspace (real Monaco editor)' : 'Run node first'}
          onClick={() => onOpenView?.('resolve-issues', { ...node.data, _gateType: gateInfo.gateType })}
        >
          <Wrench size={13} />
          Resolve Issues
        </button>

        {counts.autoFixable > 0 && (
          <button
            className="gate-action-btn"
            disabled={isActive || !canOpen}
            title="Apply all safe auto-fixes (prototype)"
            onClick={() => showActionMsg(`Would apply ${counts.autoFixable} safe fix(es) — not yet wired`)}
          >
            <CheckCircle size={13} />
            Apply Safe Fixes ({counts.autoFixable})
          </button>
        )}

        <button
          className="gate-action-btn"
          disabled={isActive || !canOpen || !onOpenView}
          title={canOpen ? 'Open Fix & Continue workspace to re-run check' : 'Run node first'}
          onClick={() => onOpenView?.('resolve-issues', { ...node.data, _gateType: gateInfo.gateType })}
        >
          <RotateCcw size={13} />
          Re-run Check
        </button>
      </div>

      {/* Action message toast */}
      {actionMsg && (
        <div className="gate-action-msg">{actionMsg}</div>
      )}

    </div>
  )
}

function ConfigField({ field, value, onChange }) {
  const handleChange = (newValue) => {
    onChange(field.key, newValue)
  }

  switch (field.type) {
    case 'text':
      return (
        <div className="config-field">
          <label>{field.label}{field.required && <span className="required">*</span>}</label>
          <input
            type="text"
            value={value || ''}
            onChange={(e) => handleChange(e.target.value)}
            placeholder={field.placeholder || ''}
          />
        </div>
      )

    case 'number':
      return (
        <div className="config-field">
          <label>{field.label}{field.required && <span className="required">*</span>}</label>
          <input
            type="number"
            value={value ?? ''}
            onChange={(e) => handleChange(e.target.valueAsNumber || 0)}
          />
        </div>
      )

    case 'boolean':
      return (
        <div className="config-field config-field-checkbox">
          <label className={`checkbox-label${POC_LOCKED ? ' poc-locked-label' : ''}`}>
            <input
              type="checkbox"
              checked={value || false}
              onChange={POC_LOCKED ? undefined : (e) => handleChange(e.target.checked)}
              disabled={POC_LOCKED}
            />
            <span>{field.label}</span>
          </label>
        </div>
      )

    case 'select': {
      const isLanguageField = field.section === 'Languages'
      return (
        <div className="config-field">
          <label>{field.label}{field.required && <span className="required">*</span>}</label>
          <select
            value={POC_LOCKED && isLanguageField ? (field.default || value || '') : (value || field.default || '')}
            onChange={POC_LOCKED ? undefined : (e) => handleChange(e.target.value)}
            disabled={POC_LOCKED}
          >
            {field.options?.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
          {POC_LOCKED && isLanguageField && (
            <p className="poc-locked-note">Locked for pilot (COBOL &rarr; Java only)</p>
          )}
        </div>
      )
    }

    case 'textarea':
      return (
        <div className="config-field">
          <label>{field.label}{field.required && <span className="required">*</span>}</label>
          <textarea
            value={value || ''}
            onChange={(e) => handleChange(e.target.value)}
            rows={3}
            placeholder={field.placeholder || ''}
          />
        </div>
      )

    default:
      return null
  }
}

function ConfigSection({ title, expanded, onToggle, children }) {
  return (
    <div className="config-section">
      <button className="config-section-header" onClick={onToggle}>
        {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        <span>{title}</span>
      </button>
      {expanded && <div className="config-section-content">{children}</div>}
    </div>
  )
}

function NodeConfigPanel({
  selectedNode, onConfigChange, onDeleteNode, onOpenView,
  executing = false, paused = false, executionProgress = {},
  accountId = null,
  applicationName = null,   // project.name — used in POST /rerun-node
  onNodeRerun = null,       // (reactNodeId, status, result) => void
}) {
  console.log("[DEBUG selectedNode]", selectedNode);
  console.log("[DEBUG selectedNode.data]", selectedNode?.data);
  console.log("[DEBUG executing]", executing);
  console.log("[DEBUG paused]", paused);
  console.log("[DEBUG executionProgress]", executionProgress);

  // Helper: Check if node has outputs/artifacts.
  // Checks in-memory executionProgress first (active session), then falls back to
  // node.data.executionResult (persisted, survives page reload / context loss).
  const nodeHasOutputs = (node) => {
    if (!node?.id) return false
    // In-memory progress from active session
    if (executionProgress[node.id]?.result) return true
    // Persisted result on node data (survives page reload)
    return Boolean(node.data?.executionResult)
  }

  const hasOutputs = nodeHasOutputs(selectedNode)
  const buttonDisabled = (executing && !paused) || !hasOutputs
  console.log("[DEBUG button logic] hasOutputs:", hasOutputs, "buttonDisabled:", buttonDisabled, "formula: (executing && !paused) || !hasOutputs =", `(${executing} && !${paused}) || !${hasOutputs}`, "=", `(${executing && !paused}) || ${!hasOutputs}`, "=", buttonDisabled)

  const [expandedSections, setExpandedSections] = useState({
    general: true,
    advanced: false,
    inputs: false,
    results: true,
  })
  const [copiedPath, setCopiedPath] = useState(null)
  // Tracks whether the last Target Profile selection pre-filled packaging fields.
  // Reset whenever the user switches to a different node.
  const [profilePrefillActive, setProfilePrefillActive] = useState(false)
  // Re-run Node state — local to this node selection
  const [rerunning, setRerunning] = useState(false)

  // Reset expanded sections when node changes
  useEffect(() => {
    setExpandedSections({
      general: true,
      advanced: false,
      inputs: false,
      results: true,
    })
    setProfilePrefillActive(false)
    setRerunning(false)
  }, [selectedNode?.id])

  // ── Re-run Node handler ────────────────────────────────────────────────────
  const handleRerunNode = async () => {
    const apiNodeId = NODE_TYPE_TO_API_ID[selectedNode.data?.nodeType]
    if (!apiNodeId || !accountId || !applicationName) return

    const userId = (() => {
      try { return JSON.parse(localStorage.getItem('modernizeit_user'))?.user_id } catch { return null }
    })()
    const apiBase = getAPIBaseUrl()
    const token = localStorage.getItem('modernizeit_token')
    const headers = {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    }

    // Capture the react node id at call time so callbacks target the right node
    const reactNodeId = selectedNode.id

    setRerunning(true)
    onNodeRerun?.(reactNodeId, 'running', null)

    try {
      // 1. Create the rerun job
      const resp = await fetch(`${apiBase}/rerun-node`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          scout_account_id: accountId,
          application_name: applicationName,
          user_id: userId,
          node_id: apiNodeId,
        }),
      })
      if (!resp.ok) {
        const errBody = await resp.json().catch(() => ({}))
        throw new Error(errBody.detail || `HTTP ${resp.status}`)
      }
      const { job_id } = await resp.json()

      // 2. Poll until terminal state (max 120 × 2 s ≈ 4 min)
      let finalJob = null
      for (let i = 0; i < 120; i++) {
        await new Promise(r => setTimeout(r, 2000))
        const jr = await fetch(`${apiBase}/jobs/${job_id}`, { headers })
        if (!jr.ok) continue
        const job = await jr.json()
        const st = (job.status || '').toUpperCase()
        if (['SUCCEEDED', 'FAILED', 'CANCELED'].includes(st)) { finalJob = job; break }
      }

      // 3. Build a minimal executionResult so gate + report panels can find the job
      const resultKey = API_ID_TO_RESULT_KEY[apiNodeId] || `${apiNodeId}_job_id`
      const newResult = {
        jobs: { [resultKey]: job_id },
        ...(finalJob?.output_data || {}),
      }
      const newStatus = finalJob?.status?.toUpperCase() === 'SUCCEEDED' ? 'complete' : 'error'
      onNodeRerun?.(reactNodeId, newStatus, newResult)
    } catch (err) {
      console.error('[Re-run Node]', err)
      onNodeRerun?.(reactNodeId, 'error', null)
    } finally {
      setRerunning(false)
    }
  }

  // Helper to copy text to clipboard
  const copyToClipboard = async (text, key) => {
    try {
      await navigator.clipboard.writeText(text)
      setCopiedPath(key)
      setTimeout(() => setCopiedPath(null), 2000)
    } catch (err) {
      console.error('Failed to copy:', err)
    }
  }

  if (!selectedNode) {
    return (
      <div className="node-config-panel empty">
        <div className="empty-state">
          <Settings size={32} />
          <h4>Node Configuration</h4>
          <p>Select a node to view and edit its settings</p>
        </div>
      </div>
    )
  }

  const nodeDef = NODE_REGISTRY[selectedNode.data?.nodeType] || {}
  const config = selectedNode.data?.config || {}

  // Check for viewable info - first by node type, then by category
  const viewableInfo = VIEWABLE_NODE_TYPES[selectedNode.data?.nodeType] || VIEWABLE_CATEGORIES[nodeDef.category]

  // Debug logging
  console.log('[NodeConfigPanel] nodeType:', selectedNode.data?.nodeType, 'viewableInfo:', viewableInfo, 'status:', selectedNode.data?.status)

  // Check for code editor - first by node type, then by category
  const hasCodeEditor = CODE_EDITOR_NODE_TYPES.includes(selectedNode.data?.nodeType) || CODE_EDITOR_CATEGORIES.includes(nodeDef.category)

  // Check if this is a file upload node
  const isUploadNode = selectedNode.data?.nodeType === 'upload-files' || selectedNode.data?.nodeType === 'pipeline-upload'

  // Check if this is a Pre-Flight node (used for targeted UI overrides)
  const isPreFlightNode = selectedNode.data?.nodeType === 'pre-flight' || selectedNode.data?.nodeType === 'pipeline-pre-flight'

  const handleUploadComplete = (result) => {
    console.log('Upload complete:', result)
  }

  // Handle staged files - store in node config for workflow execution
  const handleFileStaged = (stagedFiles) => {
    console.log('Files staged:', stagedFiles)
    // Store staged files in node config so workflow executor can access them
    onConfigChange(selectedNode.id, 'stagedFiles', stagedFiles)
  }

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }))
  }

  const handleFieldChange = (key, value) => {
    onConfigChange(selectedNode.id, key, value)
  }

  // Java Packaging prefill: when a Target Profile is selected, apply profile values
  // to java_version and spring_boot_version only. Run-time options (include_docker,
  // include_tests, run_validation) are intentionally NOT touched.
  // Prefill fires once on profile selection — user edits after that are preserved.
  const JPKG_NODE_TYPES = new Set(['generate-java-package', 'pipeline-java-package'])
  const handleProfilePrefill = (profile) => {
    if (!profile) {
      // Profile cleared — dismiss the notice but do NOT reset field values
      setProfilePrefillActive(false)
      return
    }
    let prefilled = false
    // language_version → java_version (e.g. "17", "21", "11")
    if (profile.language_version) {
      const ver = String(profile.language_version).trim()
      if (ver) { handleFieldChange('java_version', ver); prefilled = true }
    }
    // framework_version → spring_boot_version (e.g. "3.2.0", "3.1.0")
    if (profile.framework_version) {
      const ver = String(profile.framework_version).trim()
      if (ver) { handleFieldChange('spring_boot_version', ver); prefilled = true }
    }
    setProfilePrefillActive(prefilled)
  }

  const handleLabelChange = (value) => {
    onConfigChange(selectedNode.id, '_label', value)
  }

  const handleEnabledChange = (value) => {
    onConfigChange(selectedNode.id, '_enabled', value)
  }

  const handlePauseAfterChange = (value) => {
    onConfigChange(selectedNode.id, 'pause_after', value)
  }

  const handleDownloadArtifacts = async (node) => {
    // Check multiple possible locations for job_id
    let jobId = node.data?.job_id

    // Check in execution result (where DesignerView stores it)
    const executionResult = node.data?.executionResult
    if (!jobId && executionResult?.jobs) {
      const jobs = executionResult.jobs
      jobId = jobs.pre_flight_job_id ||            // Pre-Flight
              jobs.codeanalysis_job_id ||          // Code Analysis
              jobs.coderefactor_job_id ||          // Code Refactor
              jobs.test_generation_job_id ||       // Smart Tests
              jobs.java_packaging_job_id ||        // Java Packaging
              jobs.java_validation_job_id ||       // Java Validation
              jobs.service_generation_job_id ||    // Service Generation
              jobs.dependency_mapper_job_id ||     // Dependency Mapper (new)
              jobs.dependencymapper_job_id ||      // Dependency Mapper (legacy key)
              jobs.ai_naming_job_id ||             // AI Naming
              jobs.ingest_job_id ||                // Ingest
              // Fallback: find any property ending with _job_id
              Object.values(jobs).find(val => typeof val === 'string' && val.includes('_job_'))
    }

    // Fallback: check in node.data.jobs (if set directly somehow)
    if (!jobId && node.data?.jobs) {
      const jobs = node.data.jobs
      jobId = jobs.pre_flight_job_id ||            // Pre-Flight
              jobs.codeanalysis_job_id ||
              jobs.coderefactor_job_id ||
              jobs.test_generation_job_id ||
              jobs.java_packaging_job_id ||
              jobs.java_validation_job_id ||
              jobs.service_generation_job_id ||
              jobs.dependency_mapper_job_id ||
              jobs.ai_naming_job_id ||
              jobs.ingest_job_id ||
              Object.values(jobs).find(val => typeof val === 'string' && val.includes('_job_'))
    }

    // Fallback: check in-memory executionProgress (populated by NODE_COMPLETE / NODE_ERROR,
    // available in current session even before DesignerView syncs it onto node.data).
    if (!jobId && executionProgress[node.id]?.result?.jobs) {
      const jobs = executionProgress[node.id].result.jobs
      jobId = jobs.pre_flight_job_id ||            // Pre-Flight
              jobs.java_packaging_job_id ||
              jobs.java_validation_job_id ||
              jobs.codeanalysis_job_id ||
              jobs.coderefactor_job_id ||
              jobs.test_generation_job_id ||
              jobs.service_generation_job_id ||
              jobs.dependency_mapper_job_id ||
              jobs.ai_naming_job_id ||
              jobs.ingest_job_id ||
              Object.values(jobs).find(val => typeof val === 'string' && val.includes('_job_'))
    }

    if (!jobId) {
      console.error('No job ID found in node data:', {
        hasExecutionResult: !!executionResult,
        executionResultJobs: executionResult?.jobs,
        nodeDataJobs: node.data?.jobs,
        fullNodeData: node.data
      })
      alert('No job ID found for this node. Make sure the node has completed execution.')
      return
    }

    try {
      const apiUrl = getAPIBaseUrl()
      const downloadUrl = `${apiUrl}/artifacts/download/${jobId}`

      console.log('Downloading artifacts for job:', jobId)
      // Open download in new window to trigger browser download
      window.open(downloadUrl, '_blank')
    } catch (error) {
      console.error('Failed to download artifacts:', error)
      alert('Failed to download artifacts: ' + error.message)
    }
  }

  const handleDownloadPOCBundle = async (node) => {
    // Resolve job_id — same lookup as handleDownloadArtifacts
    let jobId = node.data?.job_id
    const executionResult = node.data?.executionResult
    if (!jobId && executionResult?.jobs) {
      const jobs = executionResult.jobs
      jobId = jobs.pre_flight_job_id || jobs.java_packaging_job_id ||
              Object.values(jobs).find(val => typeof val === 'string' && val.includes('_job_'))
    }
    if (!jobId && node.data?.jobs) {
      const jobs = node.data.jobs
      jobId = jobs.pre_flight_job_id || jobs.java_packaging_job_id ||
              Object.values(jobs).find(val => typeof val === 'string' && val.includes('_job_'))
    }
    if (!jobId && executionProgress[node.id]?.result?.jobs) {
      const jobs = executionProgress[node.id].result.jobs
      jobId = jobs.pre_flight_job_id || jobs.java_packaging_job_id ||
              Object.values(jobs).find(val => typeof val === 'string' && val.includes('_job_'))
    }
    if (!jobId) {
      alert('No job ID found for this node. Run the node first.')
      return
    }
    try {
      const apiUrl = getAPIBaseUrl()
      window.open(`${apiUrl}/artifacts/project-bundle/${jobId}`, '_blank')
    } catch (error) {
      console.error('Failed to download project bundle:', error)
      alert('Failed to download project bundle: ' + error.message)
    }
  }

  // Group config fields by section
  const groupedFields = {}
  nodeDef.configSchema?.forEach(field => {
    const section = field.section || 'General'
    if (!groupedFields[section]) groupedFields[section] = []
    groupedFields[section].push(field)
  })

  return (
    <div className="node-config-panel">
      <div className="config-header">
        <div className="config-header-row">
          <span className="node-type-badge" style={{ backgroundColor: nodeDef.color }}>
            {nodeDef.category?.toUpperCase()}
          </span>
          <span className="node-type-name">{nodeDef.label}</span>
        </div>
        {/* ── Downloads — always rendered first ── */}
        {/* Pre-Flight: single unified download */}
        {isPreFlightNode && nodeHasOutputs(selectedNode) && (
          <button
            className="download-artifacts-btn download-poc-bundle-btn"
            onClick={() => handleDownloadPOCBundle(selectedNode)}
            disabled={executing && !paused}
            title="Downloads a zip containing: compile reports + logs + a runnable project you can open/build locally."
          >
            <Download size={14} />
            <span>Download Deliverables</span>
          </button>
        )}
        {/* Other nodes: Build Artifacts first, then Generated Project */}
        {!isPreFlightNode && DOWNLOADABLE_NODE_TYPES.has(selectedNode.data?.nodeType) && (
          <button
            className="download-artifacts-btn"
            onClick={() => handleDownloadArtifacts(selectedNode)}
            disabled={(executing && !paused) || !nodeHasOutputs(selectedNode)}
            title={
              !nodeHasOutputs(selectedNode)
                ? 'Run node first to download build artifacts'
                : 'Download Build Artifacts — logs, reports, and output files for this step.'
            }
          >
            <Download size={14} />
            <span>Download Build Artifacts</span>
          </button>
        )}
        {!isPreFlightNode && POC_BUNDLE_NODE_TYPES.has(selectedNode.data?.nodeType) && nodeHasOutputs(selectedNode) && (
          <button
            className="download-artifacts-btn download-poc-bundle-btn"
            onClick={() => handleDownloadPOCBundle(selectedNode)}
            disabled={executing && !paused}
            title="Download Generated Project — the packaged runnable project zip you can open and build locally."
          >
            <Download size={14} />
            <span>Download Generated Project</span>
          </button>
        )}
        {/* ── Code Editor (secondary, after downloads) ── */}
        {hasCodeEditor && onOpenView && (
          <button
            className="open-view-btn code-editor-btn"
            onClick={() => onOpenView('code-editor')}
            disabled={(executing && !paused) || !nodeHasOutputs(selectedNode)}
            title={!nodeHasOutputs(selectedNode) ? 'Run node first to view code' : 'Open Code Editor'}
          >
            <Code2 size={14} />
            <span>Open Code Editor</span>
            <ExternalLink size={12} />
          </button>
        )}
        {/* ── Re-run Node — temporarily disabled on all nodes ── */}
        {NODE_TYPE_TO_API_ID[selectedNode.data?.nodeType] && applicationName && (
          <button
            className="open-view-btn"
            onClick={undefined}
            disabled={true}
            title="Re-run Node"
          >
            <RotateCcw size={14} />
            <span>Re-run Node</span>
          </button>
        )}
        {/* ── Open [View] — always last ── */}
        {viewableInfo && onOpenView && (
          <button
            className="open-view-btn"
            onClick={() => {
              console.log('[NodeConfigPanel] Opening view with node data:', selectedNode.data)
              const extraData = viewableInfo.gateType ? { _gateType: viewableInfo.gateType } : {}
              onOpenView(viewableInfo.view, { ...selectedNode.data, ...extraData })
            }}
            disabled={(executing && !paused) || !nodeHasOutputs(selectedNode)}
            title={
              !nodeHasOutputs(selectedNode)
                ? 'Run node first to view results'
                : isPreFlightNode
                  ? 'Opens the detailed compile report and fix tools.'
                  : `Opens the detailed ${viewableInfo.label.toLowerCase()} and analysis tools.`
            }
          >
            {(() => {
              const Icon = viewableInfo.icon
              return <Icon size={14} />
            })()}
            <span>Open {viewableInfo.label}</span>
            <ExternalLink size={12} />
          </button>
        )}
      </div>

      <div className="config-body">
        {/* Execution Status Banner */}
        {selectedNode.data?.status === 'error' && (
          <div className="status-banner status-error">
            <AlertCircle size={16} />
            <div className="status-banner-content">
              <strong>Execution Failed</strong>
              <p>{selectedNode.data?.error || 'An error occurred during execution'}</p>
            </div>
          </div>
        )}
        {selectedNode.data?.status === 'running' && (
          <div className="status-banner status-running">
            <Loader2 size={16} className="spin" />
            <div className="status-banner-content">
              <strong>Running...</strong>
              <p>Node is currently executing</p>
            </div>
          </div>
        )}
        {selectedNode.data?.status === 'complete' && (
          <div className="status-banner status-complete">
            <CheckCircle size={16} />
            <div className="status-banner-content">
              <strong>Complete</strong>
              <p>Node executed successfully</p>
            </div>
          </div>
        )}

        {/* Gate Status Panel — Structural Check / Compile Check nodes */}
        {GATE_NODE_TYPES[selectedNode.data?.nodeType] && (
          <GateStatusPanel
            node={selectedNode}
            gateInfo={GATE_NODE_TYPES[selectedNode.data.nodeType]}
            onOpenView={onOpenView}
            executing={executing}
            paused={paused}
            executionProgress={executionProgress}
          />
        )}

        {/* File Upload Dropzone for upload nodes */}
        {isUploadNode && (
          <FileUploadDropzone
            config={config}
            onUploadComplete={handleUploadComplete}
            onFileStaged={handleFileStaged}
          />
        )}

        {/* Node Label & Enable */}
        <ConfigSection
          title="Node Settings"
          expanded={expandedSections.general}
          onToggle={() => toggleSection('general')}
        >
          <div className="config-field">
            <label>Label</label>
            <input
              type="text"
              value={selectedNode.data?.label || ''}
              onChange={(e) => handleLabelChange(e.target.value)}
            />
          </div>

          <div className="config-field config-field-checkbox">
            <label className={`checkbox-label${POC_LOCKED ? ' poc-locked-label' : ''}`}>
              <input
                type="checkbox"
                checked={selectedNode.data?.enabled !== false}
                onChange={POC_LOCKED ? undefined : (e) => handleEnabledChange(e.target.checked)}
                disabled={POC_LOCKED}
              />
              <Power size={14} />
              <span>Enabled</span>
            </label>
          </div>

          <div className="config-field config-field-checkbox">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={selectedNode.data?.config?.pause_after === true}
                onChange={(e) => handlePauseAfterChange(e.target.checked)}
              />
              <Pause size={14} />
              <span>Pause After</span>
            </label>
            <div className="field-help">Pause workflow after this node completes</div>
          </div>

          {/* General config fields */}
          {groupedFields['General']
            ?.filter(field => !(POC_LOCKED && field.key === 'enable_ai'))
            .map(field => (
              <ConfigField
                key={field.key}
                field={field}
                value={config[field.key]}
                onChange={handleFieldChange}
              />
            ))}

          {/* Target Profile selector — Service Generation and Java Packaging only */}
          {TARGET_PROFILE_NODE_TYPES.has(selectedNode.data?.nodeType) && (
            <>
              <TargetProfileSelector
                accountId={accountId}
                value={config.target_profile_id}
                onChange={(val) => handleFieldChange('target_profile_id', val)}
                onProfileSelected={JPKG_NODE_TYPES.has(selectedNode.data?.nodeType) ? handleProfilePrefill : undefined}
                disabled={POC_LOCKED || POC_LOCKED_PROFILE_NODES.has(selectedNode.data?.nodeType)}
              />
              {JPKG_NODE_TYPES.has(selectedNode.data?.nodeType) && profilePrefillActive && (
                <div className="profile-prefill-notice">
                  ✦ Java Version and Spring Boot Version pre-filled from Target Profile
                </div>
              )}
            </>
          )}
        </ConfigSection>

        {/* Other sections */}
        {Object.entries(groupedFields).filter(([key]) => key !== 'General').map(([section, fields]) => (
          <ConfigSection
            key={section}
            title={section}
            expanded={expandedSections[section.toLowerCase()] || false}
            onToggle={() => toggleSection(section.toLowerCase())}
          >
            {fields.map(field => (
              <ConfigField
                key={field.key}
                field={field}
                value={config[field.key]}
                onChange={handleFieldChange}
              />
            ))}
          </ConfigSection>
        ))}

        {/* Inputs/Outputs info */}
        {(nodeDef.inputs?.length > 0 || nodeDef.outputs?.length > 0) && (
          <ConfigSection
            title="I/O Ports"
            expanded={expandedSections.inputs}
            onToggle={() => toggleSection('inputs')}
          >
            {nodeDef.inputs?.length > 0 && (
              <div className="io-section">
                <span className="io-label">Inputs:</span>
                {nodeDef.inputs.map(input => (
                  <div key={input.id} className="io-item">
                    <span className="io-name">{input.name}</span>
                    <span className="io-type">{input.type}</span>
                  </div>
                ))}
              </div>
            )}
            {nodeDef.outputs?.length > 0 && (
              <div className="io-section">
                <span className="io-label">Outputs:</span>
                {nodeDef.outputs.map(output => (
                  <div key={output.id} className="io-item">
                    <span className="io-name">{output.name}</span>
                    <span className="io-type">{output.type}</span>
                  </div>
                ))}
              </div>
            )}
          </ConfigSection>
        )}

        {/* Execution Results */}
        {/* Check executionProgress first (set immediately by NODE_COMPLETE / NODE_ERROR) then
            fall back to node.data.executionResult (synced by DesignerView's useEffect). */}
        {(selectedNode.data?.executionResult || executionProgress[selectedNode?.id]?.result) && (
          <ConfigSection
            title="Results"
            expanded={expandedSections.results}
            onToggle={() => toggleSection('results')}
          >
            {(() => {
              const result = selectedNode.data?.executionResult || executionProgress[selectedNode?.id]?.result
              console.log('[NodeConfigPanel] executionResult:', result)
              const formatBytes = (bytes) => {
                if (!bytes) return '0 B'
                const k = 1024
                const sizes = ['B', 'KB', 'MB', 'GB']
                const i = Math.floor(Math.log(bytes) / Math.log(k))
                return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
              }
              const formatDuration = (ms) => {
                if (!ms) return null
                if (ms < 1000) return `${ms}ms`
                return `${(ms / 1000).toFixed(2)}s`
              }

              // Determine which job ID to show — prefer the one matching this node's type
              const nodeType = selectedNode.data?.nodeType || ''
              const jobId = (
                (nodeType.includes('java-package') || nodeType.includes('java-packaging'))
                  ? result.jobs?.java_packaging_job_id
                  : nodeType.includes('java-validation')
                    ? result.jobs?.java_validation_job_id
                    : nodeType.includes('pre-flight')
                      ? result.jobs?.pre_flight_job_id
                      : (nodeType.includes('service-gen') || nodeType.includes('service-generation'))
                        ? result.jobs?.service_generation_job_id
                        : nodeType.includes('refactor')
                          ? result.jobs?.coderefactor_job_id
                          : nodeType.includes('smart-tests') || nodeType.includes('test')
                            ? result.jobs?.test_generation_job_id
                            : null
              ) || result.jobs?.pre_flight_job_id
                || result.jobs?.java_packaging_job_id
                || result.jobs?.java_validation_job_id
                || result.jobs?.service_generation_job_id
                || result.jobs?.codeanalysis_job_id
                || result.jobs?.ai_naming_job_id
                || result.jobs?.ingest_job_id
              const isAiNaming = nodeType.includes('ai-naming') || nodeType.includes('ai_naming')
              const isCodeAnalysis = !!result.jobs?.codeanalysis_job_id

              return (
                <div className="results-content">
                  {/* Error Display */}
                  {result.error && (
                    <div className="result-item result-error">
                      <span className="result-label">Error</span>
                      <div className="error-content">
                        <AlertCircle size={14} />
                        <span>{result.error}</span>
                      </div>
                    </div>
                  )}

                  {/* Success indicator for AI nodes */}
                  {result.success === false && !result.error && (
                    <div className="result-item result-error">
                      <span className="result-label">Status</span>
                      <div className="error-content">
                        <AlertCircle size={14} />
                        <span>Execution failed (no details available)</span>
                      </div>
                    </div>
                  )}

                  {/* Job ID */}
                  {jobId && (
                    <div className="result-item">
                      <span className="result-label">Job ID</span>
                      <div className="result-value copyable" onClick={() => copyToClipboard(jobId, 'job_id')}>
                        <code>{jobId}</code>
                        {copiedPath === 'job_id' ? <Check size={12} /> : <Copy size={12} />}
                      </div>
                    </div>
                  )}

                  {/* Main Program (Code Analysis) */}
                  {result.main_program && (
                    <div className="result-item">
                      <span className="result-label">Main Program</span>
                      <div className="result-value">
                        <FileCode size={14} />
                        <code>{result.main_program}</code>
                      </div>
                    </div>
                  )}

                  {/* Duration (Code Analysis) */}
                  {result.duration_ms && (
                    <div className="result-item">
                      <span className="result-label">Duration</span>
                      <div className="result-value">
                        <Clock size={14} />
                        <span>{formatDuration(result.duration_ms)}</span>
                      </div>
                    </div>
                  )}

                  {/* File Stats (Ingest) */}
                  {(result.file_count || result.file_catalog?.total_files) && (
                    <div className="result-stats">
                      <div className="stat-item">
                        <FileText size={14} />
                        <span>{result.file_count || result.file_catalog?.total_files} files</span>
                      </div>
                      {result.file_catalog?.total_size && (
                        <div className="stat-item">
                          <span>{formatBytes(result.file_catalog.total_size)}</span>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Metrics - extracted from summary data */}
                  {result.summary && Object.keys(result.summary).length > 0 && (
                    <div className="result-summary">
                      <span className="result-label">Metrics</span>
                      <div className="summary-grid">
                        {(() => {
                          // Extract useful metrics from nested summary structure
                          const extractMetrics = (summary) => {
                            const metrics = []

                            // Helper to add metric if value exists and is meaningful
                            const addMetric = (label, value, highlight = false) => {
                              if (value != null && value !== 0 && value !== '') {
                                metrics.push({ label, value, highlight })
                              }
                            }

                            // AI Naming metrics
                            if (summary.suggestions_generated != null) {
                              addMetric('Paragraphs', summary.total_paragraphs)
                              addMetric('Renamed', summary.suggestions_generated, true)
                              addMetric('Services Suggested', summary.services_suggested)
                              addMetric('High Confidence', summary.high_confidence_count)
                              addMetric('Avg Confidence', summary.avg_confidence != null ? `${(summary.avg_confidence * 100).toFixed(0)}%` : null)
                            }

                            // Code Analysis metrics (field names from runner.py)
                            addMetric('Source Lines', summary.source_lines, true)
                            addMetric('Data Items', summary.data_items)
                            addMetric('Paragraphs', summary.paragraphs)
                            addMetric('Files Analyzed', summary.files_analyzed)
                            addMetric('Java Lines', summary.java_lines)
                            addMetric('Zero Loss', summary.zero_loss === true ? 'Yes' : (summary.zero_loss === false ? 'No' : null))

                            // Code Refactor metrics (field names from runner.py)
                            addMetric('Patterns Detected', summary.patterns_detected, true)
                            addMetric('Recommendations', summary.recommendations)
                            addMetric('High Severity', summary.high_severity, true)
                            addMetric('High Priority', summary.high_priority)

                            // Discovery / ROI metrics (from runner.py executive_summary structure)
                            if (summary.executive_summary) {
                              const exec = summary.executive_summary
                              if (exec.modernization_opportunity) {
                                addMetric('Total Investment', exec.modernization_opportunity.total_investment, true)
                                addMetric('5-Year Savings', exec.modernization_opportunity.five_year_savings, true)
                                addMetric('ROI', exec.modernization_opportunity.roi_percent)
                                addMetric('Payback', exec.modernization_opportunity.payback_period)
                              }
                              if (exec.scope) {
                                addMetric('Business Processes', exec.scope.business_processes)
                                addMetric('Integration Points', exec.scope.integration_points)
                                addMetric('High Value Processes', exec.scope.high_value_processes, true)
                              }
                              if (exec.timeline) {
                                addMetric('Timeline', exec.timeline.total_duration ? `${exec.timeline.total_duration} months` : null)
                                addMetric('Phases', exec.timeline.phases)
                              }
                            }
                            // Also check for roi_analysis directly
                            if (summary.roi_analysis?.headline_metrics) {
                              const roi = summary.roi_analysis.headline_metrics
                              addMetric('5-Year ROI', roi.five_year_roi_percent ? `${roi.five_year_roi_percent.toFixed(0)}%` : null, true)
                              addMetric('Payback Months', roi.payback_months)
                            }

                            // Data Analysis metrics (from runner.py)
                            if (summary.erd) {
                              addMetric('Entities', summary.erd.total_entities, true)
                              addMetric('Relationships', summary.erd.total_relationships)
                            }
                            if (summary.data_lineage) {
                              addMetric('Data Flows', summary.data_lineage.total_flows, true)
                            }
                            if (summary.copybooks) {
                              addMetric('Copybooks', summary.copybooks.total_copybooks, true)
                              addMetric('Fields', summary.copybooks.total_fields)
                            }
                            if (summary.regex) {
                              addMetric('Data Structures', summary.regex.total_structures)
                            }
                            if (summary.ast) {
                              addMetric('Hierarchical', summary.ast.total_hierarchies)
                            }

                            // Dependency Mapper metrics (from runner.py)
                            if (summary.graph) {
                              addMetric('Programs', summary.graph.total_programs, true)
                              addMetric('Dependencies', summary.graph.total_dependencies)
                            }
                            if (summary.coupling) {
                              addMetric('Avg Coupling', summary.coupling.average_coupling?.toFixed(2))
                              addMetric('Max Coupling', summary.coupling.max_coupling)
                            }
                            if (summary.risk) {
                              addMetric('High Risk', summary.risk.high_risk_count, true)
                              addMetric('Medium Risk', summary.risk.medium_risk_count)
                            }
                            if (summary.microservices) {
                              addMetric('Services Suggested', summary.microservices.suggested_services_count)
                            }
                            if (summary.impact) {
                              addMetric('Critical Nodes', summary.impact.critical_nodes_count, true)
                            }

                            // Monolith Identifier metrics (from runner.py)
                            if (summary.patterns) {
                              addMetric('Patterns Found', summary.patterns.total_patterns, true)
                              addMetric('God Objects', summary.patterns.god_object_count)
                              addMetric('Tight Coupling', summary.patterns.tight_coupling_count)
                            }
                            if (summary.modularity) {
                              addMetric('Avg Maintainability', summary.modularity.average_maintainability?.toFixed(1))
                              addMetric('Avg Cohesion', summary.modularity.average_cohesion?.toFixed(2))
                            }
                            if (summary.business_capabilities) {
                              addMetric('Capabilities', summary.business_capabilities.total_capabilities)
                            }
                            if (summary.decomposition) {
                              addMetric('Recommended Services', summary.decomposition.recommended_services_count, true)
                            }

                            // Architecture metrics (from runner.py ArchitectureSummary)
                            addMetric('Monthly Cost', summary.estimated_monthly_cost ? `$${summary.estimated_monthly_cost.toLocaleString()}` : null, true)
                            addMetric('Application Type', summary.application_type)
                            addMetric('Primary Compute', summary.primary_compute)
                            addMetric('Primary Database', summary.primary_database)
                            addMetric('Confidence', summary.overall_confidence ? `${(summary.overall_confidence * 100).toFixed(0)}%` : null)

                            // File type counts (for Ingest) - only show if no other metrics
                            if (metrics.length === 0) {
                              Object.entries(summary).forEach(([key, val]) => {
                                if (typeof val === 'number' && val > 0) {
                                  const label = key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
                                  addMetric(label, val)
                                }
                              })
                            }

                            return metrics
                          }

                          const metrics = extractMetrics(result.summary)

                          if (metrics.length === 0) {
                            return <div className="no-metrics">No metrics available</div>
                          }

                          return metrics.map((metric, idx) => (
                            <div key={idx} className={`summary-item ${metric.highlight ? 'has-files' : ''}`}>
                              <span className="summary-type">{metric.label}</span>
                              <span className="summary-count">
                                {typeof metric.value === 'number' ? metric.value.toLocaleString() : metric.value}
                              </span>
                            </div>
                          ))
                        })()}
                      </div>
                    </div>
                  )}

                  {/* AI Naming — Before/After Rename Report */}
                  {result.naming_preview?.length > 0 && (
                    <div className="result-naming-report">
                      <span className="result-label">Naming Report — COBOL → Java</span>
                      <div className="naming-report-table">
                        <div className="naming-report-header">
                          <span>COBOL Paragraph</span>
                          <span>Java Method</span>
                          <span>Service</span>
                          <span>Conf.</span>
                        </div>
                        {result.naming_preview.map((row, idx) => (
                          <div key={idx} className={`naming-report-row ${row.confidence >= 0.9 ? 'high-conf' : row.confidence >= 0.75 ? 'med-conf' : 'low-conf'}`}>
                            <span className="naming-cobol" title={row.cobol_paragraph}>{row.cobol_paragraph}</span>
                            <span className="naming-java" title={row.java_method}>{row.java_method}</span>
                            <span className="naming-service" title={row.java_service}>{row.java_service}</span>
                            <span className="naming-conf">{(row.confidence * 100).toFixed(0)}%</span>
                          </div>
                        ))}
                        {result.summary?.suggestions_generated > 50 && (
                          <div className="naming-report-more">
                            +{result.summary.suggestions_generated - 50} more — download artifacts for full report
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Artifacts (Code Analysis) */}
                  {result.artifacts && (
                    <div className="result-artifacts">
                      <span className="result-label">Generated Artifacts</span>

                      {/* JSON Artifacts */}
                      {result.artifacts.json?.length > 0 && (
                        <div className="artifact-group">
                          <div className="artifact-header">
                            <FileCode size={12} />
                            <span>JSON Files ({result.artifacts.json.length})</span>
                          </div>
                          <div className="artifact-list">
                            {result.artifacts.json.slice(0, 5).map((file, idx) => (
                              <div key={idx} className="artifact-item">{file}</div>
                            ))}
                            {result.artifacts.json.length > 5 && (
                              <div className="artifact-item more">+{result.artifacts.json.length - 5} more</div>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Graph Artifacts */}
                      {result.artifacts.graphs?.length > 0 && (
                        <div className="artifact-group">
                          <div className="artifact-header">
                            <Image size={12} />
                            <span>Graphs ({result.artifacts.graphs.length})</span>
                          </div>
                          <div className="artifact-list">
                            {result.artifacts.graphs.map((file, idx) => (
                              <div key={idx} className="artifact-item">{file}</div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Java Project */}
                      {result.artifacts.java_project && (
                        <div className="artifact-group">
                          <div className="artifact-header">
                            <Folder size={12} />
                            <span>Java Project</span>
                          </div>
                          <div className="artifact-list">
                            <div className="artifact-item">{result.artifacts.java_project}</div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* SQL Script Downloads (IDMS SQL Generation) */}
                  {result.sql_scripts?.length > 0 && result.jobs?.idms_sql_generation_job_id && (
                    <div className="result-artifacts">
                      <span className="result-label">SQL Scripts</span>
                      <div className="artifact-group">
                        {result.sql_scripts.map((filename, idx) => (
                          <button
                            key={idx}
                            className="toolbar-btn"
                            style={{ margin: '2px 4px', fontSize: '12px' }}
                            onClick={async () => {
                              const apiBase = getAPIBaseUrl()
                              const token = localStorage.getItem('modernizeit_token') || ''
                              const headers = token ? { Authorization: `Bearer ${token}` } : {}
                              try {
                                const res = await fetch(
                                  `${apiBase}/idmssqlgeneration/${encodeURIComponent(result.jobs.idms_sql_generation_job_id)}/results/sql/${encodeURIComponent(filename)}`,
                                  { headers }
                                )
                                if (!res.ok) throw new Error(`HTTP ${res.status}`)
                                const text = await res.text()
                                const blob = new Blob([text], { type: 'text/sql' })
                                const url = URL.createObjectURL(blob)
                                const a = document.createElement('a')
                                a.href = url
                                a.download = filename
                                document.body.appendChild(a)
                                a.click()
                                document.body.removeChild(a)
                                URL.revokeObjectURL(url)
                              } catch (err) {
                                alert(`Download failed: ${err.message}`)
                              }
                            }}
                          >
                            <Download size={12} />
                            {filename}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Paths (Ingest) */}
                  {result.paths && (
                    <div className="result-paths">
                      <span className="result-label">Storage Paths</span>
                      {Object.entries(result.paths).map(([key, path]) => (
                        <div key={key} className="path-item">
                          <span className="path-label">{key.replace(/_/g, ' ')}</span>
                          <div className="path-value copyable" onClick={() => copyToClipboard(path, key)}>
                            <FolderOpen size={12} />
                            <span className="path-text">{path}</span>
                            {copiedPath === key ? <Check size={12} /> : <Copy size={12} />}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Artifacts Path (Code Analysis) */}
                  {result.artifacts_path && (
                    <div className="result-item">
                      <span className="result-label">Output Path</span>
                      <div className="result-value copyable" onClick={() => copyToClipboard(result.artifacts_path, 'artifacts_path')}>
                        <FolderOpen size={12} />
                        <span className="path-text" style={{ flex: 1 }}>{result.artifacts_path}</span>
                        {copiedPath === 'artifacts_path' ? <Check size={12} /> : <Copy size={12} />}
                      </div>
                    </div>
                  )}

                  {/* Source Hash */}
                  {result.source_hash && (
                    <div className="result-item">
                      <span className="result-label">Source Hash</span>
                      <div className="result-value copyable" onClick={() => copyToClipboard(result.source_hash, 'hash')}>
                        <code>{result.source_hash.substring(0, 16)}...</code>
                        {copiedPath === 'hash' ? <Check size={12} /> : <Copy size={12} />}
                      </div>
                    </div>
                  )}

                  {/* AI Explain - Documentation */}
                  {(result.documentation || result.output?.documentation) && (
                    <div className="result-item ai-result">
                      <span className="result-label">Documentation</span>
                      <div className="ai-result-content">
                        <pre className="ai-output-text">{result.documentation || result.output?.documentation}</pre>
                        <button
                          className="copy-ai-btn"
                          onClick={() => copyToClipboard(result.documentation || result.output?.documentation, 'documentation')}
                        >
                          {copiedPath === 'documentation' ? <Check size={12} /> : <Copy size={12} />}
                          {copiedPath === 'documentation' ? 'Copied!' : 'Copy'}
                        </button>
                      </div>
                    </div>
                  )}

                  {/* AI Transform - Code */}
                  {(result.transformed_code || result.output?.transformed_code) && (
                    <div className="result-item ai-result">
                      <span className="result-label">Transformed Code</span>
                      <div className="ai-result-content">
                        <pre className="ai-output-code">{result.transformed_code || result.output?.transformed_code}</pre>
                        <button
                          className="copy-ai-btn"
                          onClick={() => copyToClipboard(result.transformed_code || result.output?.transformed_code, 'transformed')}
                        >
                          {copiedPath === 'transformed' ? <Check size={12} /> : <Copy size={12} />}
                          {copiedPath === 'transformed' ? 'Copied!' : 'Copy'}
                        </button>
                      </div>
                    </div>
                  )}

                  {/* AI Review - Findings */}
                  {(result.review || result.output?.review) && (() => {
                    const reviewData = result.review || result.output?.review
                    return (
                      <div className="result-item ai-result">
                        <span className="result-label">Code Review</span>
                        <div className="ai-result-content">
                          {reviewData.summary && <p className="review-summary">{reviewData.summary}</p>}
                          {reviewData.findings?.length > 0 && (
                            <div className="review-findings">
                              {reviewData.findings.slice(0, 10).map((finding, idx) => (
                                <div key={idx} className={`finding-item severity-${finding.severity}`}>
                                  <span className="finding-severity">{finding.severity}</span>
                                  <span className="finding-issue">{finding.issue}</span>
                                </div>
                              ))}
                              {reviewData.findings.length > 10 && (
                                <div className="findings-more">+{reviewData.findings.length - 10} more findings</div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    )
                  })()}

                  {/* AI Chat - Response */}
                  {result.response && (
                    <div className="result-item ai-result">
                      <span className="result-label">AI Response</span>
                      <div className="ai-result-content">
                        <pre className="ai-output-text">{result.response}</pre>
                        <button
                          className="copy-ai-btn"
                          onClick={() => copyToClipboard(result.response, 'response')}
                        >
                          {copiedPath === 'response' ? <Check size={12} /> : <Copy size={12} />}
                          {copiedPath === 'response' ? 'Copied!' : 'Copy'}
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Debug: Raw Result (always shown for debugging) */}
                  <div className="result-item">
                    <span className="result-label">Raw Result (Debug)</span>
                    <div className="ai-result-content">
                      <pre className="ai-output-text" style={{ fontSize: '10px', maxHeight: '150px' }}>
                        {JSON.stringify(result, null, 2)}
                      </pre>
                    </div>
                  </div>
                </div>
              )
            })()}
          </ConfigSection>
        )}
      </div>

      <div className="config-footer">
        <button
          className="delete-btn"
          onClick={POC_LOCKED ? undefined : () => onDeleteNode(selectedNode.id)}
          disabled={POC_LOCKED}
          title={POC_LOCKED ? 'Node deletion is locked for POC' : undefined}
        >
          <Trash2 size={14} />
          Delete Node
        </button>
      </div>
    </div>
  )
}

export default NodeConfigPanel
